import Api from "./api";
import { project } from "../hooks/auth";

/**
 * Registro de telas vistas, enviado em lote para POST /usage/screens.
 *
 * - Junta as telas por até FLUSH_INTERVAL_MS e manda tudo numa requisição; ao
 *   esconder a aba ou sair da página, manda na hora.
 * - Usa fetch com keepalive, e não o axios: o envio sobrevive ao fechamento da
 *   aba e uma falha aqui nunca abre toast nem redireciona para o login (o
 *   interceptor do axios faria as duas coisas).
 * - Falha silenciosa: uso não medido é aceitável, navegação quebrada não.
 *
 * O estado fica no módulo, e não no componente, para que remontar o Layout
 * (ou o StrictMode em dev) não conte a mesma tela duas vezes.
 */

const FLUSH_INTERVAL_MS = 15000;
const MAX_BATCH = 50;

// Aba que ficou escondida por mais que isso e volta a ser vista conta como
// nova visualização: muita gente deixa o produto aberto e volta a ele no dia
// seguinte sem navegar.
const REVISIT_AFTER_HIDDEN_MS = 30 * 60 * 1000;

let queue = [];
let flushTimer = null;
let listenersAttached = false;
let lastTrackedKey = null;
let current = null;
let hiddenSince = null;

const resolveAuthorizationHeader = () => {
  const headerFromDefaults =
    Api?.defaults?.headers?.authorization ||
    Api?.defaults?.headers?.common?.authorization ||
    Api?.defaults?.headers?.Authorization ||
    Api?.defaults?.headers?.common?.Authorization;

  if (headerFromDefaults) return String(headerFromDefaults);

  const token = localStorage.getItem(`${project}:token`);
  return token ? `Bearer ${token}` : "";
};

const send = (screens) => {
  const authorization = resolveAuthorizationHeader();
  if (!authorization) return;

  const baseURL = String(Api?.defaults?.baseURL || "").replace(/\/+$/, "");

  try {
    fetch(`${baseURL}/usage/screens`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", Authorization: authorization },
      body: JSON.stringify({ screens }),
    }).catch(() => {});
  } catch {
    // fetch indisponível ou keepalive recusado: descarta.
  }
};

export const flushUsage = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  while (queue.length > 0) {
    send(queue.splice(0, MAX_BATCH));
  }
};

const enqueue = (screen) => {
  queue.push(screen);

  if (queue.length >= MAX_BATCH) {
    flushUsage();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushUsage, FLUSH_INTERVAL_MS);
  }
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "hidden") {
    hiddenSince = Date.now();
    flushUsage();
    return;
  }

  const wasHiddenLong = hiddenSince && Date.now() - hiddenSince >= REVISIT_AFTER_HIDDEN_MS;
  hiddenSince = null;
  if (wasHiddenLong && current) enqueue(current.screen);
};

const attachListeners = () => {
  if (listenersAttached || typeof document === "undefined") return;
  listenersAttached = true;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", flushUsage);
};

/**
 * Registra a tela atual. Mudar só filtros na query string (mesma tela, mesmo
 * caminho) não conta de novo; trocar de relatório/aba ou de caminho conta.
 */
export const trackScreen = (screen, pathname) => {
  current = screen ? { screen, pathname } : null;
  if (!screen) {
    // Passou por uma página fora do catálogo: voltar para a anterior é nova visita.
    lastTrackedKey = null;
    return;
  }

  const key = `${screen}|${pathname}`;
  if (key === lastTrackedKey) return;
  lastTrackedKey = key;

  attachListeners();
  enqueue(screen);
};
